/**
 * Voucher Redemption Service
 * Handles public voucher validation and redemption
 */

import { anonSupabase, supabase } from "./supabase";
import {
  conversionRedeemBlockReason,
  redeemableValue,
} from "../utils/conversionVoucher";
import {
  buildFacilityRedemptionCandidates,
  normalizeFacilityIds,
  type FacilityLocationRow,
  type FacilityOrganizationRow,
  type FacilityRedemptionCandidates,
} from "./facilityRedemptionCandidates";

/**
 * Calculate distance between two coordinates using Haversine formula
 * @returns Distance in miles
 */
function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Classify a thrown error or PostgREST error response as either:
 *   - 'network': transport-layer failure that may succeed on retry
 *     (TypeError, "Failed to fetch", AbortError, timeout, etc.)
 *   - 'business': PostgREST returned an error code, or the request
 *     completed but the server rejected the operation (already redeemed,
 *     cancelled, invalid code). These will not succeed on retry without
 *     intervention.
 *
 * The retry-queue uses this classification to decide whether to enqueue a
 * failed redemption for later automatic retry.
 */
function classifyError(err: unknown): "network" | "business" {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "network";
  }
  if (err instanceof TypeError) {
    // Browser fetch failures (CORS, DNS, offline) come through as TypeError.
    return "network";
  }
  if (err && typeof err === "object") {
    const e = err as { name?: string; message?: string; code?: string };
    if (e.name === "AbortError" || e.name === "TimeoutError") {
      return "network";
    }
    const msg = (e.message || "").toLowerCase();
    if (
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("network error") ||
      msg.includes("load failed") ||
      msg.includes("timeout")
    ) {
      return "network";
    }
    // PostgREST surfaces a string `code` field for business errors (PGRST*,
    // 23xxx, 42xxx). Presence of a code means the server replied — business.
    if (e.code) {
      return "business";
    }
  }
  return "business";
}

export interface VoucherValidationResult {
  valid: boolean;
  error?: string;
  /**
   * On `valid=false`, classifies the failure so callers can decide whether
   * to enqueue the redemption for retry. Absent on `valid=true`.
   */
  failureKind?: "network" | "business";
  /** Optional header for the failure card (e.g. "Not ready yet" for a gated ticket reward). */
  title?: string;
  voucher?: {
    voucher_code: string;
    discount_value: number;
    voucher_name: string;
    end_date: number;
    start_date: number;
    is_redeemed: boolean;
    redeemed_at?: string;
    organization_name?: string;
    organization_id?: string;
    event_name?: string;
    event_id?: string;
    event_type?: string; // Event type: show, class, seminar, conference, webinar
    cta_id?: string; // event_call_to_actions ID if voucher was sourced from an event CTA
    org_cta_id?: string; // organization_ctas ID if voucher was sourced from an org CTA (xor with cta_id)
    // For org-level vouchers with multiple locations
    available_locations?: Array<{
      id: string;
      name: string;
    }>;
    requires_location_selection?: boolean;
    // For multi-org vouchers (redeemable at multiple or all organizations)
    redemption_scope?:
      | "SINGLE_ORGANIZATION"
      | "MULTIPLE_ORGANIZATIONS"
      | "ALL_ORGANIZATIONS";
    available_organizations?: Array<{
      id: string;
      name: string;
      address?: string;
      city?: string;
      state?: string;
      distance?: number; // Distance in miles from recipient (if geographic filter applied)
      organization_id?: string; // Actual org UUID for FK (differs from id when entry is a location)
      location_id?: string; // Location UUID when this entry represents a location within an org
      organization_name?: string; // Parent organization (shared-facility picker)
    }>;
    requires_organization_selection?: boolean;
    /**
     * R1 (map-listed redemption plan v4 §3.3): ALL / MULTIPLE offer vouchers no longer preload the partner list
     * (PostgREST capped it at 1,000 rows). The page searches the server instead (searchRedemptionEntries) and
     * `available_organizations` is left empty.
     */
    organization_search?: boolean;
    // Voucher source/reason for display
    voucher_source?: string;
    /** The voucher's facility IDs (shared-facility picker, 2026-10-25). */
    facility_ids?: string[];
    /**
     * The facility ID is held by MORE THAN ONE organization (incident 2026-10-24): the partner must pick the
     * store. `available_organizations` then holds the shared-facility candidates (see facilityRedemptionCandidates).
     */
    facility_shared?: boolean;
    /**
     * The organization (and store, when the facility is a single store) this voucher's facility resolves to when
     * that is UNAMBIGUOUS. The page sends it with the redemption so the server records the store; the server still
     * re-checks it against the facility mapping.
     */
    redemption_context?: { organization_id: string; location_id?: string };
  };
}

interface VoucherUpdateData {
  is_redeemed: boolean;
  redeemed_at: string;
  redeemed_by: string | null;
  redemption_source: string;
  applied_amount: number;
  booth_id?: string;
  vendor_name?: string;
  redemption_location_id?: string;
  redeeming_organization_id?: string; // For multi-org vouchers
}

export interface RedeemVoucherResult {
  success: boolean;
  message: string;
  discountValue?: number;
  voucherCode?: string;
  appliedAmount?: number;
  /**
   * On `success=false`, classifies the failure so callers (notably the
   * retry queue) can decide whether to enqueue / retry / give up. Absent
   * on `success=true`.
   */
  failureKind?: "network" | "business";
  /**
   * Set to true when the failure was specifically "already redeemed". The
   * retry queue treats this as a success-with-note for queued retries,
   * because either we (or someone else) already completed the redemption.
   */
  alreadyRedeemed?: boolean;
  /** The server's rejection code (redeem_voucher_guarded error_code), when it gave one. */
  errorCode?: string;
}

/**
 * Log a voucher activity event (fire-and-forget, never blocks caller)
 */
function logVoucherActivity(
  eventType: string,
  voucherCode: string,
  result: string,
  voucherId?: string,
  details?: Record<string, unknown>,
): void {
  supabase
    .from("voucher_activity_log")
    .insert({
      event_type: eventType,
      voucher_code: voucherCode.toUpperCase(),
      voucher_id: voucherId || null,
      result,
      details: details || null,
    })
    .then(({ error }) => {
      if (error) {
        console.warn("Failed to log voucher activity:", error.message);
      }
    });
}

/**
 * Log a voucher activity with IP address (fire-and-forget)
 * Used from the public /redeem page where IP tracking is available
 */
export function logVoucherActivityWithIP(
  eventType: string,
  voucherCode: string,
  result: string,
  voucherId?: string,
  details?: Record<string, unknown>,
  ipAddress?: string,
): void {
  const enrichedDetails = {
    ...(details || {}),
    ...(ipAddress ? { ip_address: ipAddress } : {}),
  };
  logVoucherActivity(
    eventType,
    voucherCode,
    result,
    voucherId,
    enrichedDetails,
  );
}

/**
 * Validate a voucher code without redeeming it
 */
export async function validateVoucher(
  code: string,
  clientIp?: string,
): Promise<VoucherValidationResult> {
  try {
    console.log("Validating voucher:", code);

    // Exact-code lookup via the scoped, rate-limited voucher-lookup edge fn (service-role) instead of a
    // direct anon `from("vouchers")` read — so the wide-open vouchers SELECT RLS can be locked down
    // (security audit: direct anon/partner reads allowed enumerating all voucher codes + emails).
    const { data: lookup, error } = await supabase.functions.invoke(
      "voucher-lookup",
      {
        body: { code: code.toUpperCase() },
      },
    );

    if (error) {
      console.error("Error querying voucher:", error);
      return {
        valid: false,
        failureKind: classifyError(error),
        error: "Error validating voucher. Please try again.",
      };
    }
    const voucher = (
      lookup as { voucher: Record<string, unknown> | null } | null
    )?.voucher as VoucherValidationResult["voucher"] | null | undefined;

    if (!voucher) {
      console.log("Voucher not found:", code);
      logVoucherActivity(
        "lookup_not_found",
        code,
        "not_found",
        undefined,
        clientIp ? { ip_address: clientIp } : undefined,
      );
      return {
        valid: false,
        failureKind: "business",
        error: "Invalid voucher code. Please check and try again.",
      };
    }

    console.log("Voucher found:", voucher.voucher_code);

    // Check if cancelled
    if (voucher.is_cancelled || voucher.status === "cancelled") {
      logVoucherActivity(
        "validation_failed",
        code,
        "cancelled",
        voucher.id,
        clientIp ? { ip_address: clientIp } : undefined,
      );
      return {
        valid: false,
        failureKind: "business",
        error: "This voucher has been cancelled and is no longer valid.",
      };
    }

    // Check if marked as test
    if (voucher.status === "test") {
      logVoucherActivity(
        "validation_failed",
        code,
        "test_voucher",
        voucher.id,
        clientIp ? { ip_address: clientIp } : undefined,
      );
      return {
        valid: false,
        failureKind: "business",
        error: "This is a test voucher and cannot be redeemed.",
      };
    }

    // Check if already redeemed
    if (voucher.is_redeemed) {
      logVoucherActivity(
        "validation_failed",
        code,
        "already_redeemed",
        voucher.id,
        clientIp ? { ip_address: clientIp } : undefined,
      );
      return {
        valid: false,
        failureKind: "business",
        error: "This voucher has already been redeemed.",
      };
    }

    // Conversion-engine gate (Stage 1): a conversion voucher is code-only until a
    // catalyst activates it. Reject when not yet active. Legacy vouchers
    // (conversion_engine_id null) are unaffected — guardrail #1.
    const conversionBlock = conversionRedeemBlockReason(voucher);
    if (conversionBlock) {
      logVoucherActivity(
        "validation_failed",
        code,
        "not_active",
        voucher.id,
        clientIp ? { ip_address: clientIp } : undefined,
      );
      return {
        valid: false,
        failureKind: "business",
        error: conversionBlock,
      };
    }

    // Check if voucher has expired
    const now = Date.now();
    if (voucher.end_date && voucher.end_date < now) {
      logVoucherActivity(
        "validation_failed",
        code,
        "expired",
        voucher.id,
        clientIp ? { ip_address: clientIp } : undefined,
      );
      return {
        valid: false,
        failureKind: "business",
        error: "This voucher has expired.",
      };
    }

    // Check if voucher is not yet valid
    if (voucher.start_date && voucher.start_date > now) {
      logVoucherActivity(
        "validation_failed",
        code,
        "not_yet_valid",
        voucher.id,
        clientIp ? { ip_address: clientIp } : undefined,
      );
      return {
        valid: false,
        failureKind: "business",
        error: "This voucher is not yet valid.",
      };
    }

    // Multi-org voucher detection (via offer)
    let redemptionScope:
      | "SINGLE_ORGANIZATION"
      | "MULTIPLE_ORGANIZATIONS"
      | "ALL_ORGANIZATIONS" = "SINGLE_ORGANIZATION";
    let availableOrganizations: Array<{
      id: string;
      name: string;
      city?: string;
      state?: string;
      address?: string;
      distance?: number;
      organization_id?: string;
      location_id?: string;
      organization_name?: string;
    }> = [];
    let requiresOrganizationSelection = false;
    let organizationSearch = false;
    let voucherSourceDescription: string | undefined = undefined;

    // Check if voucher's offer has multi-org redemption scope
    if (voucher.offer_id) {
      try {
        const { data: offer, error: offerError } = await supabase
          .from("offers")
          .select(
            "redemption_scope, voucher_source_description, eligibility_radius_miles",
          )
          .eq("id", voucher.offer_id)
          .maybeSingle();

        if (!offerError && offer) {
          voucherSourceDescription = offer.voucher_source_description;

          if (offer.redemption_scope) {
            redemptionScope = offer.redemption_scope;

            const voucherFacilityIds = (voucher as { facility_ids?: unknown })
              .facility_ids;
            const hasFacilityIds =
              Array.isArray(voucherFacilityIds) &&
              voucherFacilityIds.length > 0;
            if (
              (redemptionScope === "ALL_ORGANIZATIONS" ||
                redemptionScope === "MULTIPLE_ORGANIZATIONS") &&
              !hasFacilityIds
            ) {
              // R1: searched server-side (redeem-picker-search); same eligible set as the old preload, no
              // 1,000-row cap. The old client-side radius branch was provably inert (anon reads no contacts;
              // no offer has a radius) and is not carried over.
              requiresOrganizationSelection = true;
              organizationSearch = true;
            } else if (
              redemptionScope === "ALL_ORGANIZATIONS" ||
              redemptionScope === "MULTIPLE_ORGANIZATIONS"
            ) {
              // Legacy hybrid (offer + facility_ids): unchanged preload path.
              requiresOrganizationSelection = true;

              // Single RPC call replaces all org/location queries + filtering
              const { data: orgs, error: orgsError } = await supabase.rpc(
                "get_redemption_organizations",
                { p_offer_id: voucher.offer_id },
              );

              if (!orgsError && orgs) {
                let orgList = orgs as Array<{
                  id: string;
                  name: string;
                  address: string;
                  city: string;
                  state: string;
                  organization_id: string;
                  location_id: string | null;
                }>;

                // Apply distance filter client-side if offer has radius
                if (offer.eligibility_radius_miles && voucher.contact_id) {
                  const { data: contact } = await supabase
                    .from("contacts")
                    .select("latitude, longitude")
                    .eq("id", voucher.contact_id)
                    .maybeSingle();

                  if (contact?.latitude && contact?.longitude) {
                    const contactLat = parseFloat(contact.latitude);
                    const contactLng = parseFloat(contact.longitude);
                    const radiusMiles = parseFloat(
                      offer.eligibility_radius_miles,
                    );

                    // Fetch org coordinates for distance calculation
                    const orgIds = [
                      ...new Set(orgList.map((o) => o.organization_id)),
                    ];
                    const { data: orgCoords } = await supabase
                      .from("organizations")
                      .select("id, latitude, longitude")
                      .in("id", orgIds)
                      .not("latitude", "is", null)
                      .not("longitude", "is", null);

                    if (orgCoords && orgCoords.length > 0) {
                      const coordMap = new Map(
                        orgCoords.map((oc) => [oc.id, oc]),
                      );
                      orgList = orgList
                        .map((o) => {
                          const coords = coordMap.get(o.organization_id);
                          if (!coords) {
                            return null;
                          }
                          const distance = calculateDistance(
                            contactLat,
                            contactLng,
                            parseFloat(coords.latitude),
                            parseFloat(coords.longitude),
                          );
                          return distance <= radiusMiles
                            ? { ...o, distance }
                            : null;
                        })
                        .filter(Boolean) as typeof orgList;

                      orgList.sort(
                        (a, b) =>
                          ((a as any).distance || 0) -
                          ((b as any).distance || 0),
                      );

                      console.log(
                        `Distance filter: ${radiusMiles}mi from contact, ${orgList.length} orgs within range`,
                      );
                    }
                  }
                }

                availableOrganizations = orgList.map((o) => ({
                  id: o.id,
                  name: o.name,
                  address: o.address,
                  city: o.city,
                  state: o.state,
                  organization_id: o.organization_id,
                  location_id: o.location_id || undefined,
                  distance: (o as any).distance,
                }));
              }

              console.log(
                `Multi-org voucher: scope=${redemptionScope}, orgs=${availableOrganizations.length}`,
              );
            }
          }
        }
      } catch (offerError) {
        console.warn("Error checking offer redemption scope:", offerError);
      }
    }

    // Mig 1540: an Event in a Box statewide / nationwide voucher (voucher-lookup adds redemption_area). The store is
    // picked from the area's locator stores (redeem-picker-search); during the issuing event's window the show may
    // redeem it without a store, as today, so the picker is not required then.
    const redemptionArea = (
      voucher as {
        redemption_area?: {
          kind?: string;
          state_name?: string;
          booth_open?: boolean;
        } | null;
      }
    ).redemption_area;
    if (redemptionArea && !requiresOrganizationSelection) {
      organizationSearch = true;
      requiresOrganizationSelection = redemptionArea.booth_open !== true;
    }

    // Look up organization/location by facility_id
    let organizationName = "Unknown Organization";
    let organizationId: string | undefined = undefined;
    let availableLocations: Array<{ id: string; name: string }> = [];
    let requiresLocationSelection = false;
    let facilityShared = false;
    let redemptionContext:
      | { organization_id: string; location_id?: string }
      | undefined = undefined;

    if (
      voucher.facility_ids &&
      Array.isArray(voucher.facility_ids) &&
      voucher.facility_ids.length > 0
    ) {
      if (voucher.facility_ids.length === 1) {
        // Single facility_id — look up the one retailer
        const facilityId = voucher.facility_ids[0];

        // Shared facility (incident 2026-10-24): when this facility ID is held by MORE THAN ONE organization the
        // server cannot tell who is redeeming (context_required), so the partner picks the store up front. Skipped
        // when an offer picker is already shown. A failed read leaves sharedCheck null: no picker here (the server's
        // context_required still brings it up) and, below, NO context is sent -- an unverified guess never is.
        const sharedCheck = requiresOrganizationSelection
          ? null
          : await fetchFacilityRedemptionCandidates([facilityId]);
        // Store choice is needed when the facility ID maps to more than one organization (the guard's own ambiguity
        // test) OR to more than one live store of one organization (e.g. PROD 16111: on the organization AND on one
        // of its three stores -- the store-level lookup below would otherwise pin every redemption to that one store).
        if (
          sharedCheck &&
          sharedCheck.candidates.length > 0 &&
          (sharedCheck.organizationIds.length > 1 ||
            sharedCheck.candidates.length > 1)
        ) {
          facilityShared = true;
          requiresOrganizationSelection = true;
          availableOrganizations = sharedCheck.candidates.map((c) => ({
            id: c.id,
            name: c.name,
            address: c.address,
            city: c.city,
            state: c.state,
            organization_id: c.organization_id,
            location_id: c.location_id,
            organization_name: c.organization_name,
          }));
          organizationName = "Choose the store below";
          console.log(
            `facility_id ${facilityId}: ${sharedCheck.organizationIds.length} organization(s), ${sharedCheck.candidates.length} stores - requiring a store choice`,
          );
        } else if (
          sharedCheck &&
          sharedCheck.organizationIds.length === 1 &&
          sharedCheck.candidates.length === 1
        ) {
          // Exactly one organization and exactly one place to redeem: that is known, so it is sent (the server
          // re-checks it). Derived ONLY from the verified candidate list, never from a single-row lookup.
          const only = sharedCheck.candidates[0];
          redemptionContext = {
            organization_id: only.organization_id,
            ...(only.location_id ? { location_id: only.location_id } : {}),
          };
        }

        if (!facilityShared) {
          try {
            console.log(`Looking up facility_id: ${facilityId}`);

            // First, try to find a location with this facility_id
            const { data: location, error: locError } = await supabase
              .from("locations")
              .select("id, name, organization_id")
              .eq("facility_id", facilityId)
              .maybeSingle();

            if (locError) {
              console.warn(
                `Location query error for facility_id ${facilityId}:`,
                locError,
              );
            }

            if (location) {
              organizationName = location.name;
              organizationId = location.organization_id;
              console.log(
                `Found location for facility_id ${facilityId}:`,
                location.name,
              );
            } else {
              console.log(
                `No location found for facility_id ${facilityId}, trying organizations table`,
              );

              const { data: org, error: orgError } = await supabase
                .from("organizations")
                .select("id, name")
                .eq("facility_id", facilityId)
                .maybeSingle();

              if (orgError) {
                console.warn(
                  `Organization query error for facility_id ${facilityId}:`,
                  orgError,
                );
              }

              if (org) {
                organizationName = org.name;
                organizationId = org.id;
                console.log(
                  `Found organization for facility_id ${facilityId}:`,
                  org.name,
                );

                // Check if this organization has multiple locations
                const { data: orgLocations, error: locsError } = await supabase
                  .from("locations")
                  .select("id, name")
                  .eq("organization_id", org.id)
                  .eq("is_archived", false)
                  .order("name", { ascending: true });

                if (locsError) {
                  console.warn(
                    `Error fetching locations for org ${org.id}:`,
                    locsError,
                  );
                } else if (orgLocations && orgLocations.length > 1) {
                  availableLocations = orgLocations.map((loc) => ({
                    id: loc.id,
                    name: loc.name,
                  }));
                  requiresLocationSelection = true;
                  console.log(
                    `Organization ${org.name} has ${orgLocations.length} locations - requiring selection`,
                  );
                }
              } else {
                console.warn(
                  `Could not find location or organization for facility_id: ${facilityId}`,
                );
              }
            }
          } catch (lookupError) {
            console.error("Error looking up organization:", lookupError);
          }
        }
      } else {
        // Multiple facility_ids — look up ALL retailers and let the user choose
        console.log(
          `Voucher has ${voucher.facility_ids.length} facility_ids, looking up all retailers`,
        );

        try {
          // Look up locations matching any of the facility_ids
          const { data: locations, error: locsError } = await supabase
            .from("locations")
            .select(
              "id, name, organization_id, facility_id, address_line1, city, state",
            )
            .in("facility_id", voucher.facility_ids);

          const matchedFacilityIds = new Set<string>();
          if (!locsError && locations) {
            for (const loc of locations) {
              matchedFacilityIds.add(loc.facility_id);
              availableOrganizations.push({
                id: loc.organization_id,
                name: loc.name,
                address: loc.address_line1 || undefined,
                city: loc.city || undefined,
                state: loc.state || undefined,
              });
            }
          }

          // For any facility_ids not found in locations, check organizations table
          const unmatchedIds = voucher.facility_ids.filter(
            (fid: string) => !matchedFacilityIds.has(fid),
          );
          if (unmatchedIds.length > 0) {
            const { data: orgs, error: orgsError } = await supabase
              .from("organizations")
              .select(
                "id, name, facility_id, address_line1, address_city, address_state",
              )
              .in("facility_id", unmatchedIds);

            if (!orgsError && orgs) {
              for (const org of orgs) {
                availableOrganizations.push({
                  id: org.id,
                  name: org.name,
                  address: org.address_line1 || undefined,
                  city: org.address_city || undefined,
                  state: org.address_state || undefined,
                });
              }
            }
          }

          if (availableOrganizations.length > 0) {
            requiresOrganizationSelection = true;
            // Set display name to indicate multiple retailers
            organizationName =
              voucherSourceDescription || "Participating Retailers";
            console.log(
              `Found ${availableOrganizations.length} retailers for ${voucher.facility_ids.length} facility_ids`,
            );
          } else {
            console.warn(
              `Could not find any retailers for facility_ids: ${voucher.facility_ids.join(", ")}`,
            );
          }
        } catch (lookupError) {
          console.error("Error looking up multiple retailers:", lookupError);
        }
      }
    }

    // Look up event name and type if voucher is linked to an event.
    //
    // eventId is set from voucher.event_id directly (source of truth) so the
    // frontend's isEventVoucher check at VoucherRedemption.tsx:74 stays true
    // even when the events lookup fails. Previously, eventId was only set
    // inside the `if (event)` branch — meaning a voucher whose event was
    // soft-deleted (events.deleted_at non-null, RLS-hidden from the user)
    // would return event_id: undefined to the frontend and trigger the
    // wrong field ("Redeemed By" instead of "Vendor Redemption #") with
    // no numeric validation. Surfaced 2026-05-30 with DEMO1234 → event
    // 155b3e38-... (deleted 2026-05-01).
    let eventName: string | undefined = undefined;
    const eventId: string | undefined = voucher.event_id || undefined;
    let eventType: string | undefined = undefined;

    if (voucher.event_id) {
      try {
        console.log(`Looking up event_id: ${voucher.event_id}`);
        const { data: event, error: eventError } = await supabase
          .from("events")
          .select("id, title, type")
          .eq("id", voucher.event_id)
          .maybeSingle();

        if (eventError) {
          console.warn(
            `Event query error for event_id ${voucher.event_id}:`,
            eventError,
          );
        }

        if (event) {
          eventName = event.title;
          // eventId already set from voucher.event_id above; reassigning here
          // would still produce the same value but the unconditional fallback
          // is what guarantees isEventVoucher stays true even when this
          // lookup fails.
          eventType = event.type;
          console.log(
            `Found event for event_id ${voucher.event_id}:`,
            event.title,
            "type:",
            event.type,
          );
        } else {
          console.warn(
            `Could not find event for event_id: ${voucher.event_id} (likely soft-deleted or RLS-hidden); voucher will still render as an event voucher per the canonical voucher.event_id signal.`,
          );
        }
      } catch (eventLookupError) {
        console.error("Error looking up event:", eventLookupError);
        // Continue without event name - don't fail validation
      }
    }

    // Determine voucher source for display (priority: offer description > event name > issue notes)
    // voucher_name is intentionally excluded — it's often a generic label (e.g. "$20 Voucher")
    // that duplicates what's already shown in the amount + org name display
    const voucherSource =
      voucherSourceDescription || eventName || voucher.issue_notes;

    // Log successful validation. (last_validated_at is now stamped service-role inside the voucher-lookup
    // edge fn, so the client no longer writes vouchers directly as anon.)
    logVoucherActivity("validated", code, "valid", voucher.id, {
      discount_value: voucher.discount_value,
      organization_name: organizationName,
      organization_id: organizationId,
      ...(clientIp ? { ip_address: clientIp } : {}),
    });

    return {
      valid: true,
      voucher: {
        voucher_code: voucher.voucher_code,
        // Redeemable amount: conversion vouchers redeem against the escalated
        // current_value (ledger projection); legacy vouchers against discount_value.
        // Downstream redeemVoucher caps/defaults against this field.
        discount_value: redeemableValue(voucher),
        voucher_name: voucher.voucher_name,
        end_date: voucher.end_date,
        start_date: voucher.start_date,
        is_redeemed: voucher.is_redeemed,
        redeemed_at: voucher.redeemed_at,
        organization_name: organizationName,
        organization_id: organizationId,
        event_name: eventName,
        event_id: eventId,
        event_type: eventType,
        cta_id: voucher.cta_id,
        org_cta_id: voucher.org_cta_id,
        // For org-level vouchers with multiple locations
        available_locations:
          availableLocations.length > 0 ? availableLocations : undefined,
        requires_location_selection: requiresLocationSelection,
        // For multi-org vouchers (redeemable at multiple or all organizations)
        redemption_scope: redemptionScope,
        available_organizations:
          availableOrganizations.length > 0
            ? availableOrganizations
            : undefined,
        requires_organization_selection: requiresOrganizationSelection,
        organization_search: organizationSearch,
        // Voucher source/reason for display
        voucher_source: voucherSource,
        facility_ids: normalizeFacilityIds(voucher.facility_ids),
        facility_shared: facilityShared,
        redemption_context: redemptionContext,
      },
    };
  } catch (error) {
    console.error("Error validating voucher:", error);
    return {
      valid: false,
      failureKind: classifyError(error),
      error: "An error occurred while validating the voucher.",
    };
  }
}

export interface RedeemVoucherInput {
  voucher_code: string;
  redeemed_by?: string;
  applied_amount?: number;
  booth_id?: string;
  vendor_name?: string;
  redemption_location_id?: string; // For org-level vouchers with multiple locations
  redeeming_organization_id?: string; // For multi-org vouchers
  client_ip?: string; // IP address from /redeem page
  /**
   * R1 wire v2 (plan v4 §2.7): the picked entry. The server derives the (organization, location) pair from it and
   * refuses a contradicting pair; the pair above is ALWAYS sent too, so an older server that ignores these fields
   * still records the same attribution. Absent on v1 payloads (older builds / older queued items).
   */
  wire_version?: 2;
  entry_id?: string;
}

/**
 * Request body for the voucher-redeem edge function. v1 (older builds and older queued items: no entry_id) or
 * v2 (plan v4 §2.7: entry_id + the pair, which is always sent so an older server records the same attribution).
 */
export function buildRedeemRequestBody(
  data: RedeemVoucherInput,
  appliedAmount: number | undefined,
): Record<string, unknown> {
  return {
    voucher_code: data.voucher_code.toUpperCase(),
    applied_amount: appliedAmount,
    redeemed_by: data.redeemed_by || null,
    redemption_source: "Retailer Portal",
    booth_id: data.booth_id,
    vendor_name: data.vendor_name,
    redemption_location_id: data.redemption_location_id,
    redeeming_organization_id: data.redeeming_organization_id,
    ...(data.wire_version === 2 && data.entry_id
      ? { wire_version: 2, entry_id: data.entry_id }
      : {}),
  };
}

/**
 * Redeem a voucher code
 */
/**
 * Map a redeem_voucher_guarded error_code (surfaced by the voucher-redeem edge fn) to a clear,
 * redeemer-facing message. NOTE: 'already_redeemed' is handled separately (it sets alreadyRedeemed);
 * this covers the OTHER rejection reasons that previously all showed "already redeemed".
 */
function redeemErrorCodeMessage(code?: string): string {
  switch (code) {
    case "entry_mismatch":
    case "entry_unknown":
    case "invalid_entry":
      return "The selected organization could not be confirmed. Please select it again and resubmit.";
    case "wrong_scope":
      return "This voucher can't be redeemed at the selected location or organization.";
    case "context_required":
    case "needs_reselection":
      return "This voucher's retailer ID is listed under more than one store. Choose the store where it is being redeemed, then press Redeem again.";
    case "expired":
      return "This voucher has expired.";
    case "not_yet_valid":
      return "This voucher isn't valid yet.";
    case "not_active":
      return "This voucher isn't active yet.";
    case "reward_pending":
      return "This reward hasn't been unlocked yet — the member needs to check in first.";
    case "reward_void":
      return "This reward is no longer available.";
    case "cancelled":
      return "This voucher has been cancelled and is no longer valid.";
    case "test":
      return "This is a test voucher and cannot be redeemed.";
    case "not_found":
      return "Invalid voucher code. Please check and try again.";
    case "invalid_location":
      return "The selected location is invalid.";
    case "invalid_organization":
      return "The selected organization is invalid.";
    case "attribution_mismatch":
      return "The selected location doesn't belong to the selected organization.";
    case "invalid_amount":
      return "The applied amount is invalid.";
    default:
      return "This voucher can't be redeemed right now. Please see a USLS representative.";
  }
}

export async function redeemVoucher(
  data: RedeemVoucherInput,
): Promise<RedeemVoucherResult> {
  try {
    // First validate the voucher
    console.log("Validating voucher:", data.voucher_code);
    const validation = await validateVoucher(data.voucher_code, data.client_ip);

    if (!validation.valid || !validation.voucher) {
      console.log("Validation failed:", validation.error);
      // Distinguish "already redeemed" so the retry queue can mark
      // queued items as success-with-note (the prior attempt completed,
      // possibly from this device or a duplicate submission).
      const isAlreadyRedeemed =
        validation.error === "This voucher has already been redeemed.";
      return {
        success: false,
        message: validation.error || "Invalid voucher",
        failureKind: validation.failureKind ?? "business",
        ...(isAlreadyRedeemed ? { alreadyRedeemed: true } : {}),
      };
    }

    // Event vouchers: the vendor's BUSINESS NAME is required (reverted from the numeric booth #
    // on 2026-08-12 — vendors are reimbursed by business name). Service-level guard mirrors the
    // form-level requirement so callers that bypass the UI (future integrations, retried payloads
    // from the offline queue, etc.) can't slip through without a business name.
    if (validation.voucher.event_id) {
      const businessName = (data.vendor_name ?? "").trim();
      if (!businessName) {
        return {
          success: false,
          failureKind: "business",
          message: "Business Name is required for event vouchers.",
        };
      }
    }

    // Validate applied amount if provided
    if (data.applied_amount !== undefined && data.applied_amount !== null) {
      // Check if applied amount is a valid number
      if (isNaN(data.applied_amount) || data.applied_amount <= 0) {
        return {
          success: false,
          failureKind: "business",
          message: "Applied amount must be a positive number.",
        };
      }

      // Check if applied amount exceeds voucher amount
      if (data.applied_amount > validation.voucher.discount_value) {
        return {
          success: false,
          failureKind: "business",
          message: `Applied amount ($${data.applied_amount.toFixed(2)}) cannot exceed voucher amount ($${validation.voucher.discount_value.toFixed(2)}).`,
        };
      }

      // Validate max 2 decimal places
      if (!/^\d+(\.\d{1,2})?$/.test(data.applied_amount.toString())) {
        return {
          success: false,
          failureKind: "business",
          message: "Applied amount can have at most 2 decimal places.",
        };
      }
    }

    console.log("Voucher validated, attempting redemption...");

    // Mark voucher as redeemed
    const updateData: VoucherUpdateData = {
      is_redeemed: true,
      redeemed_at: new Date().toISOString(),
      redeemed_by: data.redeemed_by || null,
      redemption_source: "Retailer Portal",
      applied_amount: data.applied_amount || validation.voucher.discount_value, // Use applied amount or default to full voucher amount
    };

    // Add booth_id and vendor_name if provided (for Show/Conference events)
    if (data.booth_id) {
      updateData.booth_id = data.booth_id;
    }
    if (data.vendor_name) {
      updateData.vendor_name = data.vendor_name;
    }
    // Add redemption_location_id if provided (for org-level vouchers with multiple locations)
    if (data.redemption_location_id) {
      updateData.redemption_location_id = data.redemption_location_id;
    }
    // Add redeeming_organization_id if provided (for multi-org vouchers)
    if (data.redeeming_organization_id) {
      updateData.redeeming_organization_id = data.redeeming_organization_id;
    }

    // Guarded redemption write via the service-role voucher-redeem edge fn (race-safe flip) instead of a
    // direct anon UPDATE — so the broad anon vouchers UPDATE/SELECT policies can be removed (security audit).
    const { data: redeemResult, error } = await supabase.functions.invoke(
      "voucher-redeem",
      { body: buildRedeemRequestBody(data, updateData.applied_amount) },
    );

    if (error) {
      console.error("Supabase error during redemption:", error);
      // A 400 from the R1 decoder (entry_mismatch / entry_unknown / invalid_entry) is a business refusal with a
      // specific, fixable cause -- never queued for retry.
      const ctx = (error as { context?: { json?: () => Promise<unknown> } })
        .context;
      if (ctx && typeof ctx.json === "function") {
        const b = (await ctx.json().catch(() => null)) as {
          errorCode?: string;
        } | null;
        if (
          b?.errorCode === "entry_mismatch" ||
          b?.errorCode === "entry_unknown" ||
          b?.errorCode === "invalid_entry"
        ) {
          return {
            success: false,
            failureKind: "business",
            message: redeemErrorCodeMessage(b.errorCode),
          };
        }
      }
      return {
        success: false,
        failureKind: classifyError(error),
        message: `Redemption error: ${error.message || "Unknown error"}`,
      };
    }

    const rr = redeemResult as {
      voucher: Record<string, unknown> | null;
      alreadyRedeemed?: boolean;
      errorCode?: string;
    } | null;
    const voucherData = rr?.voucher as
      | (VoucherUpdateData & { id: string; voucher_code: string })
      | null;

    // Only a genuine already-redeemed maps to that message.
    if (rr?.alreadyRedeemed) {
      return {
        success: false,
        failureKind: "business",
        alreadyRedeemed: true,
        message: "This voucher has already been redeemed.",
      };
    }
    // Any OTHER server rejection returns voucher:null WITH an errorCode. Previously this branch also
    // said "already redeemed" for every null voucher — which mislabeled wrong_scope / expired /
    // not-active / reward-pending etc. (prod incident: an org-level voucher redeemed at a chosen
    // location returned wrong_scope and showed "already redeemed"). Surface the real reason.
    if (!voucherData) {
      return {
        success: false,
        failureKind: "business",
        message: redeemErrorCodeMessage(rr?.errorCode),
        ...(rr?.errorCode ? { errorCode: rr.errorCode } : {}),
      };
    }

    console.log("Voucher redeemed successfully:", voucherData.voucher_code);

    // Log successful redemption (fire-and-forget)
    logVoucherActivity(
      "redeemed",
      data.voucher_code,
      "success",
      voucherData.id,
      {
        applied_amount: voucherData.applied_amount,
        redeemed_by: data.redeemed_by,
        vendor_name: data.vendor_name,
        ...(data.client_ip ? { ip_address: data.client_ip } : {}),
      },
    );

    return {
      success: true,
      message: "Voucher redeemed!",
      discountValue: voucherData.discount_value,
      voucherCode: voucherData.voucher_code,
      appliedAmount: voucherData.applied_amount,
    };
  } catch (error: unknown) {
    console.error("Unexpected error redeeming voucher:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Please try again";
    return {
      success: false,
      failureKind: classifyError(error),
      message: `An unexpected error occurred: ${errorMessage}`,
    };
  }
}

// ============================================================================
// Event Ticket reward redemption (Phase C) — a TK- ticket code redeems its
// linked reward voucher through the narrow server adapter (resolve/redeem_
// ticket_reward). The internal reward voucher_code is never exposed; the holder
// uses only the ticket code. The live voucher_code path above is unchanged.
// ============================================================================

/** Validate a TK- ticket reward for /redeem; maps resolve_ticket_reward into the validation shape. */
export async function validateTicketReward(
  code: string,
): Promise<VoucherValidationResult> {
  const { data, error } = await supabase.rpc("resolve_ticket_reward", {
    p_ticket_code: code,
  });
  if (error) {
    return {
      valid: false,
      error: "Could not look up that reward. Please try again.",
      failureKind: "network",
    };
  }
  const d = data as {
    found?: boolean;
    status?: string;
    reward_state?: string;
    already_redeemed?: boolean;
    cancelled?: boolean;
    checked_in?: boolean;
    redeemable_value?: number | null;
    window_start_ms?: number | null;
    window_end_ms?: number | null;
    is_gun_show?: boolean;
    event_id?: string | null;
    event_type?: string | null;
    /** Added 2026-09-10-016 so an org-less event can still name what was redeemed. */
    event_title?: string | null;
    organization_id?: string | null;
    organization_name?: string | null;
  } | null;
  if (!d?.found) {
    return {
      valid: false,
      error: "We couldn't find a reward for that code.",
      failureKind: "business",
    };
  }
  // The server computes a single `status`; the UI just maps it to a message (#5).
  const status = d.status ?? "";
  if (status === "redeemable") {
    // A ticket reward reads its redemption scope from the placeholder ENVELOPE offer (offer_id),
    // which is ALL_ORGANIZATIONS by design (F5: 2026-06-29-017) — the assigned reward offer's scope
    // is intentionally NOT used for redemption. So the reward is redeemable at any org; the operator
    // captures the Business Name for reimbursement. (True retailer-scoping needs a dedicated mechanism
    // that enforces a target org in redeem_ticket_reward — see the flex-scoping design note.)
    // Retail (non-gun-show) ticket whose event has an organization: /redeem shows that org name and
    // records it as the redeemer (no free-text Business Name). Gun-show / org-less tickets keep the
    // event-voucher Business-Name flow (is_gun_show true, or organization_id absent). We deliberately
    // do NOT set voucher.event_id here — that would flip isEventVoucher and disturb the existing flow;
    // the retail branch is driven off is_gun_show + organization_id instead.
    return {
      valid: true,
      voucher: {
        voucher_code: code.toUpperCase(),
        discount_value: Number(d.redeemable_value ?? 0),
        voucher_name: "Event Reward",
        // Show the ticket's ACTUAL redemption window (defaults to the event day) — not a hardcoded
        // now+1yr, which misrepresented the expiry on /redeem (EIB feedback 2026-08-14). A NULL window
        // means no expiration on the backend, so pass 0 → the UI renders "No Expiration" rather than
        // fabricating a date (EIB audit).
        start_date: d.window_start_ms ?? 0,
        end_date: d.window_end_ms ?? 0,
        is_redeemed: false,
        redemption_scope: "ALL_ORGANIZATIONS",
        requires_organization_selection: false,
        requires_location_selection: false,
        // Event context, ALWAYS exposed for a ticket. The retail decision downstream is
        // "a ticket whose event is not a gun show", which is a property of the EVENT and
        // must not depend on whether an organization happens to be linked: on PROD
        // events.organization_id is NULL for all 146 events and only 77 have a junction
        // row, so gating the retail path on org presence meant it never engaged and every
        // ticket was asked for a Business Name (the reported bug). event_name doubles as
        // the redeemer label when the event genuinely has no organization.
        event_id: d.event_id ?? undefined,
        event_type: d.event_type ?? undefined,
        event_name: d.event_title ?? undefined,
        // The org is still exposed ONLY for a NON-gun-show event that HAS one. A gun show
        // — even one with an org — keeps the Business Name flow. Unchanged.
        ...(d.is_gun_show !== true && d.organization_id
          ? {
              organization_id: d.organization_id,
              organization_name: d.organization_name ?? undefined,
            }
          : {}),
      },
    };
  }
  const unlockTime =
    typeof d.window_start_ms === "number"
      ? new Date(d.window_start_ms).toLocaleTimeString("en-US", {
          timeZone: "America/Chicago",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;
  const message =
    status === "cancelled"
      ? "This reward is no longer available."
      : status === "already_redeemed"
        ? "This reward has already been redeemed."
        : status === "not_checked_in"
          ? "Please check in at the event first — your reward unlocks after you check in."
          : status === "not_yet"
            ? unlockTime
              ? `This reward isn't available yet — it unlocks at ${unlockTime}.`
              : "This reward isn't available to redeem yet. Please check back during the redemption window."
            : status === "expired"
              ? "This reward has expired."
              : "This reward isn't ready to redeem yet — please check back soon.";
  // A gated ticket reward is not "invalid" — give the failure card a friendlier header.
  const title =
    status === "not_checked_in" || status === "not_yet" || status === "pending"
      ? "Not ready yet"
      : status === "already_redeemed"
        ? "Already redeemed"
        : status === "cancelled"
          ? "No longer available"
          : undefined;
  return { valid: false, error: message, failureKind: "business", title };
}

/** Redeem a TK- ticket reward via the narrow server adapter. */
export async function redeemTicketReward(params: {
  code: string;
  redeeming_organization_id?: string;
  redemption_location_id?: string;
  applied_amount?: number;
  redeemed_by?: string;
}): Promise<{ success: boolean; error?: string; applied_amount?: number }> {
  const { data, error } = await supabase.rpc("redeem_ticket_reward", {
    p_ticket_code: params.code,
    p_redeeming_organization_id: params.redeeming_organization_id ?? null,
    p_location_id: params.redemption_location_id ?? null,
    p_applied_amount: params.applied_amount ?? null,
    p_redeemed_by: params.redeemed_by ?? null,
  });
  if (error) {
    return { success: false, error: "Redemption failed. Please try again." };
  }
  const d = data as {
    redeemed?: boolean;
    applied_value?: number;
    error?: string;
  } | null;
  if (d?.redeemed) {
    return { success: true, applied_amount: Number(d.applied_value ?? 0) };
  }
  return {
    success: false,
    error: d?.error ?? "This reward could not be redeemed.",
  };
}

/** Raw check-in-aware reward status for a TK- ticket (used to show the reward inline on /t/:code). */
export interface TicketRewardStatus {
  found: boolean;
  // not_checked_in | pending | not_yet | expired | redeemable | already_redeemed | cancelled
  status?: string;
  checked_in?: boolean;
  redeemable_value?: number | null;
  window_start_ms?: number | null;
  window_end_ms?: number | null;
  // Per-offer redemption instruction (offers.redemption_instructions); null = use the default.
  redemption_message?: string | null;
}

/** Server-computed reward status for a ticket code (anon-safe; no PII, no internal voucher code). */
export async function getTicketRewardStatus(
  code: string,
): Promise<TicketRewardStatus> {
  const { data, error } = await supabase.rpc("resolve_ticket_reward", {
    p_ticket_code: code,
  });
  if (error || !data) {
    return { found: false };
  }
  const d = data as TicketRewardStatus & { found?: boolean };
  return {
    found: !!d.found,
    status: d.status,
    checked_in: d.checked_in,
    redeemable_value: d.redeemable_value ?? null,
    window_start_ms: d.window_start_ms ?? null,
    window_end_ms: d.window_end_ms ?? null,
    redemption_message: d.redemption_message ?? null,
  };
}

/** True when a scanned/typed code is an Event Ticket (TK-) rather than a voucher code. */
export function isTicketCode(code: string): boolean {
  return /^TK-/i.test((code || "").trim());
}

/** One row of the /redeem partner search (redeem-picker-search). */
export interface RedemptionSearchEntry {
  entry_id: string;
  organization_id: string;
  location_id: string | null;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  type_label: string | null;
  facility_id_match: boolean;
}

export type RedemptionSearchCursor = { r: number; n: string; e: string };

export interface RedemptionSearchPage {
  status: "ok" | "not_found" | "not_applicable" | "rate_limited" | "error";
  items: RedemptionSearchEntry[];
  has_more: boolean;
  next_cursor: RedemptionSearchCursor | null;
}

/**
 * R1 (plan v4 §3.3): server-side, paged search of the partners an ALL / MULTIPLE offer voucher can be redeemed
 * at. Same eligible set as the old preload (the server calls get_redemption_organizations), without the 1,000-row
 * cap. Instructor street addresses come back NULL.
 */
export async function searchRedemptionEntries(
  code: string,
  q: string,
  cursor: RedemptionSearchCursor | null = null,
  limit = 25,
): Promise<RedemptionSearchPage> {
  const { data, error } = await supabase.functions.invoke(
    "redeem-picker-search",
    { body: { code: code.trim().toUpperCase(), q, cursor, limit } },
  );
  if (error || !data) {
    return { status: "error", items: [], has_more: false, next_cursor: null };
  }
  const d = data as Partial<RedemptionSearchPage> & { status?: string };
  return {
    status: (d.status as RedemptionSearchPage["status"]) ?? "error",
    items: Array.isArray(d.items) ? d.items : [],
    has_more: Boolean(d.has_more),
    next_cursor: d.next_cursor ?? null,
  };
}

/**
 * Read the shared-facility candidates for a voucher's facility IDs (anon-readable organizations/locations, the same
 * rows the redeem guard reads). Returns null when a read fails, so a caller never treats an unverified lookup as
 * "unambiguous". See facilityRedemptionCandidates.ts for the entry rule and why the server stays authoritative.
 * Uses the ANON client on purpose: a CRM user signed in on this browser would read through their own RLS
 * (has_org_access) and could miss a holder the SECURITY DEFINER guard sees; the anon SELECT policies on
 * organizations/locations are `true`, matching the guard's view.
 */
export async function fetchFacilityRedemptionCandidates(
  facilityIds: unknown,
): Promise<FacilityRedemptionCandidates | null> {
  const ids = normalizeFacilityIds(facilityIds);
  if (ids.length === 0) {
    return null;
  }
  // Raw values too: the legacy guard compares facility_id exactly (no trim).
  const raw = Array.isArray(facilityIds)
    ? (facilityIds as unknown[]).filter(
        (x): x is string => typeof x === "string" && !!x,
      )
    : [];
  const match = [...new Set([...ids, ...raw])];
  try {
    const [locRes, orgRes] = await Promise.all([
      anonSupabase
        .from("locations")
        .select(
          "id, name, organization_id, facility_id, is_archived, address_line1, city, state",
        )
        .in("facility_id", match)
        .limit(500),
      anonSupabase
        .from("organizations")
        .select(
          "id, name, facility_id, is_archived, address_line1, address_city, address_state",
        )
        .in("facility_id", match)
        .limit(500),
    ]);
    if (locRes.error || orgRes.error) {
      console.warn(
        "Shared-facility lookup failed:",
        locRes.error || orgRes.error,
      );
      return null;
    }
    const facilityLocations = (locRes.data ?? []) as FacilityLocationRow[];
    const facilityOrgs = (orgRes.data ?? []) as FacilityOrganizationRow[];
    const parentIds = [
      ...new Set(
        facilityLocations.map((l) => l.organization_id).filter(Boolean),
      ),
    ];
    const orgIds = facilityOrgs.map((o) => o.id);
    const [parentRes, childRes] = await Promise.all([
      parentIds.length
        ? anonSupabase
            .from("organizations")
            .select(
              "id, name, is_archived, address_line1, address_city, address_state",
            )
            .in("id", parentIds)
            .limit(500)
        : Promise.resolve({ data: [], error: null }),
      orgIds.length
        ? anonSupabase
            .from("locations")
            .select(
              "id, name, organization_id, facility_id, is_archived, address_line1, city, state",
            )
            .in("organization_id", orgIds)
            .limit(1000)
        : Promise.resolve({ data: [], error: null }),
    ]);
    if (parentRes.error || childRes.error) {
      console.warn(
        "Shared-facility lookup failed:",
        parentRes.error || childRes.error,
      );
      return null;
    }
    return buildFacilityRedemptionCandidates(
      ids,
      facilityLocations,
      facilityOrgs,
      (parentRes.data ?? []) as FacilityOrganizationRow[],
      (childRes.data ?? []) as FacilityLocationRow[],
    );
  } catch (e) {
    console.warn("Shared-facility lookup failed:", e);
    return null;
  }
}
